import { useCallback } from 'react'
import { useCurrentClient } from '@mysten/dapp-kit-react'
import { useQueryClient } from '@tanstack/react-query'
import { ClientWithCoreApi } from '@mysten/sui/client'
import { toBase64 } from '@mysten/sui/utils'

import { GetWalletPositionsResponseType } from '@/shared/backend/types'
import { isPosition, isPositionCap } from '@/shared/gen/kai-leverage/position-core-clmm/structs'
import { positionsQueryKey } from '@/shared/kai/hooks/useUserPositions'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'
import { CreatedObject } from 'utils/useExecute'

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

/**
 * Fetches BCS data for an object with retries.
 * Sui RPC nodes can lag behind after tx execution — the object may not be
 * available immediately (getObject throws). Retries up to 3 times with 100ms delay.
 * Returns the base64-encoded Move struct BCS and its type.
 */
async function fetchBcsWithRetry(client: ClientWithCoreApi, objectId: string) {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const { object } = await client.core.getObject({ objectId, include: { content: true } })
      if (object.content) {
        return { bcsBytes: toBase64(object.content), type: object.type }
      }
    } catch {
      // Object not available yet (RPC lag) — fall through to retry.
    }
    if (attempt < 2) {
      await delay(100)
    }
  }
  return undefined
}

/**
 * Updates an existing position's BCS data in the
 * `['positions', walletAddress]` React Query cache by fetching from RPC.
 * The tx has already succeeded on-chain — this updates the cache before
 * the indexer catches up.
 *
 * Pass `{ deleted: true }` to mark a position as closed without an RPC fetch.
 */
export const usePreProcessedPositionUpdate = () => {
  const client = useCurrentClient()
  const queryClient = useQueryClient()
  const walletAddress = useCurrentWalletAddress()

  return useCallback(
    async (positionId: string, opts?: { deleted?: boolean }) => {
      if (!walletAddress) {
        return
      }

      const cacheKey = positionsQueryKey(walletAddress)

      if (opts?.deleted) {
        queryClient.setQueryData(cacheKey, (old: GetWalletPositionsResponseType | undefined) =>
          old?.map(p =>
            p.positionId === positionId
              ? { ...p, isDeleted: true, deletionTimestampMs: Date.now() }
              : p
          )
        )
        return
      }

      try {
        const bcsData = await fetchBcsWithRetry(client, positionId)
        if (bcsData) {
          queryClient.setQueryData(cacheKey, (old: GetWalletPositionsResponseType | undefined) =>
            old?.map(p =>
              p.positionId === positionId ? { ...p, lastPositionBcs: bcsData.bcsBytes } : p
            )
          )
        }
      } catch {
        // RPC fetch failed — position will update after indexer catches up
      }
    },
    [client, queryClient, walletAddress]
  )
}

/**
 * Injects a newly created position into the
 * `['positions', walletAddress]` React Query cache.
 * The tx has already succeeded on-chain — this makes the position visible
 * in the UI before the indexer catches up.
 *
 * Extracts position and cap IDs from the tx's created objects, fetches BCS
 * from RPC, and appends the entry.
 */
export const usePreProcessedPositionCreate = () => {
  const client = useCurrentClient()
  const queryClient = useQueryClient()
  const walletAddress = useCurrentWalletAddress()

  return useCallback(
    async (createdObjects?: CreatedObject[]): Promise<string | undefined> => {
      if (!walletAddress) {
        return undefined
      }

      const createdPosition = createdObjects?.find(o => isPosition(o.objectType))
      const createdCap = createdObjects?.find(o => isPositionCap(o.objectType))

      if (!createdPosition || !createdCap) {
        return undefined
      }

      try {
        const bcsData = await fetchBcsWithRetry(client, createdPosition.objectId)
        if (bcsData) {
          queryClient.setQueryData(
            positionsQueryKey(walletAddress),
            (old: GetWalletPositionsResponseType | undefined) =>
              old
                ? [
                    ...old,
                    {
                      positionId: createdPosition.objectId,
                      positionType: bcsData.type,
                      positionCapId: createdCap.objectId,
                      lastPositionBcs: bcsData.bcsBytes,
                      creationTimestampMs: Date.now(),
                      isDeleted: false,
                    },
                  ]
                : undefined
          )
          return createdPosition.objectId
        }
      } catch {
        // RPC fetch failed — position will appear after indexer catches up
      }
      return undefined
    },
    [client, queryClient, walletAddress]
  )
}
